// Add / edit a client contact at ≤880px (mobile overhaul Phase C §7.1 D,
// lower sheet) — the sheet form of ClientDetail's inline ContactForm. It
// owns only its draft; the page owns the POST/PATCH/DELETE, so both widths
// write the same way. `contactFields` is shared with the desktop form.

import { useState } from 'react';
import type { ClientContactRow } from '../../shared/types';
import { Seg } from './primitives';
import { Sheet, SheetButton, SheetRow } from './Sheet';
import { TrashIcon } from './icons';

export type ContactDraft = { name: string; role_label: string; email: string; phone: string };

export function contactDraftFrom(contact?: ClientContactRow): ContactDraft {
  return {
    name: contact?.name ?? '',
    role_label: contact?.role_label ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
  };
}

export function contactFields(draft: ContactDraft, makeDefault: boolean): Record<string, unknown> {
  const fields: Record<string, unknown> = {
    name: draft.name.trim(),
    role_label: draft.role_label.trim() || null,
    email: draft.email.trim() || null,
    phone: draft.phone.trim() || null,
  };
  if (makeDefault) fields.is_default = true;
  return fields;
}

export function ContactSheet({
  contact,
  deleteBlocked = false,
  onSave,
  onDelete,
  onClose,
}: {
  /** Absent = add mode. */
  contact?: ClientContactRow;
  /** The live default with siblings — routes/clients.ts keeps one default,
      so another contact has to take the badge first. */
  deleteBlocked?: boolean;
  onSave: (fields: Record<string, unknown>) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(() => contactDraftFrom(contact));
  const [makeDefault, setMakeDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A default can't be unset from this form, only replaced by promoting
  // another contact — so the segment disappears while editing it.
  const isDefault = contact?.is_default === 1;

  const set = (key: keyof ContactDraft) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  const submit = async () => {
    if (busy || !draft.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(contactFields(draft, makeDefault));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save contact.');
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onDelete || deleteBlocked || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact.');
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      variant="half"
      title={contact ? 'Edit contact' : 'Add contact'}
      context={contact?.role_label ?? undefined}
      onClose={() => {
        if (!busy) onClose();
      }}
      leftAction={
        <SheetButton onClick={onClose} disabled={busy}>
          Cancel
        </SheetButton>
      }
      rightAction={
        <SheetButton tone="accent" onClick={() => void submit()} disabled={busy || !draft.name.trim()}>
          {contact ? 'Save' : 'Add'}
        </SheetButton>
      }
    >
      <form
        className="contact-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="f-field contact-pair">
          <span className="f-label">
            Name <span className="req">*</span>
          </span>
          <input className="f-input" value={draft.name} onChange={set('name')} disabled={busy} autoFocus />
        </div>
        <div className="f-field contact-pair">
          <span className="f-label">Role</span>
          <input
            className="f-input"
            value={draft.role_label}
            onChange={set('role_label')}
            placeholder="Marketing lead…"
            disabled={busy}
          />
        </div>
        <div className="f-field contact-pair">
          <span className="f-label">Email</span>
          <input
            className="f-input mono"
            type="email"
            value={draft.email}
            onChange={set('email')}
            placeholder="name@…"
            disabled={busy}
          />
        </div>
        <div className="f-field contact-pair">
          <span className="f-label">Phone</span>
          <input
            className="f-input mono"
            type="tel"
            value={draft.phone}
            onChange={set('phone')}
            placeholder="04…"
            disabled={busy}
          />
        </div>
        {!isDefault && (
          <div className="f-field">
            <span className="f-label">Default</span>
            <Seg
              options={[
                { value: 'no', label: 'No' },
                { value: 'yes', label: 'Make default' },
              ]}
              value={makeDefault ? 'yes' : 'no'}
              onChange={(v) => setMakeDefault(v === 'yes')}
            />
          </div>
        )}
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </form>
      {contact && onDelete && (
        <div className="contact-delete-row">
          <SheetRow
            icon={<TrashIcon />}
            label="Delete contact"
            tone="danger"
            disabled={deleteBlocked || busy}
            sub={deleteBlocked ? 'Make another contact the default first' : undefined}
            onSelect={() => void remove()}
          />
        </div>
      )}
    </Sheet>
  );
}
